<template>
  <div class="my-account-content">
    <div class="my-account-details-container">
      <div class="my-account-details-container-header">
        <div class="title-text">
          Edit Your Information
        </div>
        <div class="actions">
          <LockingButton
            class="delete-button"
            :locked="deleting"
            :text="'Delete Account'"
            @click="onDeleteAccountClicked"
          />
          <LockingButton
            class="save-button"
            :disabled="!isDirty"
            :locked="saving"
            :text="'Save'"
            @click="onSaveAccountInformationClicked"
          />
        </div>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && accountInformation"
        class="my-account-details-container-content"
      >
        <div class="my-account-details-flex-container-side-by-side">
          <TextInput
            v-model="accountInformation.firstName"
            :show-required="true"
            label="First Name"
            class="first-item"
          />
          <TextInput
            v-model="accountInformation.lastName"
            :show-required="true"
            label="Last Name"
            class="second-item"
          />
        </div>
        <div
          v-if="accountInformation.hasCustomerRecord"
          class="my-account-details-flex-container-side-by-side"
        >
          <PhoneNumberInput
            v-model="accountInformation.phoneNumber"
            class="first-item"
          />
          <TextInput
            v-model="accountInformation.addressLine1"
            label="Address Line 1"
            class="second-item"
          />
        </div>
        <div
          v-if="accountInformation.hasCustomerRecord"
          class="my-account-details-flex-container-side-by-side"
        >
          <TextInput
            v-model="accountInformation.addressLine2"
            label="Address Line 2"
            class="first-item"
          />
          <TextInput
            v-model="accountInformation.city"
            label="City"
            class="second-item"
          />
        </div>
        <div
          v-if="accountInformation.hasCustomerRecord"
          class="my-account-details-flex-container-side-by-side"
        >
          <Dropdown
            v-model="accountInformation.stateId"
            label="State"
            :options="stateDropdownOptions"
            class="first-item"
          />
          <TextInput
            v-model="accountInformation.postalCode"
            label="Postal Code"
            class="second-item"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { AccountInformationDetails } from '@/@types/responses/miscellaneous/AccountInformationDetails'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import LockingButton from '@/components/shared/LockingButton.vue'
import { MiscellaneousService } from '@/services/miscellaneous.service'
import { AccountInformationMessage } from '@/@types/messages/AccountInformationMessage'
import { LOCAL_STORAGE_CONSTANTS } from '@/constants/localstorage.contstants'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { StateService } from '@/services/states.service'

export default defineComponent({
  name: 'MyAccount',
  components: {
    Dropdown,
    LoadingPulse,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  setup() {
    const miscellaneousService = inject(PROVIDER_CONSTANTS.miscellaneousService) as MiscellaneousService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      miscellaneousService,
      stateService,
      toast
    }
  },
  data() {
    let accountInformation: AccountInformationDetails|undefined
    let unchangedAccountInformation: AccountInformationDetails|undefined

    return {
      accountInformation,
      deleting: false,
      loading: true,
      saving: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedAccountInformation
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.accountInformation
      const b = this.unchangedAccountInformation

      return (a?.firstName !== b?.firstName ||
              a?.lastName !== b?.lastName ||
              a?.phoneNumber !== b?.phoneNumber ||
              a?.addressLine1 !== b?.addressLine1 ||
              a?.addressLine2 !== b?.addressLine2 ||
              a?.city !== b?.city ||
              a?.stateId !== b?.stateId ||
              a?.postalCode !== b?.postalCode) && (a?.firstName.trim() !== '' && a?.lastName.trim() !== '')
    }
  },
  mounted() {
    this.getStates().then(() => {
      this.getAccountInformation()
    })
  },
  methods: {
    getAccountInformation(): Promise<void> {
      return this.miscellaneousService.getAccountInformation().then(accountInformation => {
        this.accountInformation = accountInformation
        this.unchangedAccountInformation = JSON.parse(JSON.stringify(accountInformation))
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your account information. Try reloading the page.')
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onDeleteAccountClicked(): void {
      this.deleting = true

      this.miscellaneousService.deleteAccount().then(() => {
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.accessToken, '')
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.isEmployee, 'false')
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.refreshToken, '')
        window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CONSTANTS.eventName))
        
        this.deleting = false

        this.$router.push('/')
        
        this.toast.success('Your account was deleted successfully.')
      }).catch(error => {
        this.deleting = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to delete your account. Please try again.')
      })
    },
    onSaveAccountInformationClicked(): void {
      if (!this.accountInformation) return

      this.saving = true

      const accountInformationMessage = new AccountInformationMessage(this.accountInformation.firstName, this.accountInformation.lastName, this.accountInformation.phoneNumber, this.accountInformation.addressLine1, this.accountInformation.addressLine2, this.accountInformation.city, this.accountInformation.stateId, this.accountInformation.postalCode)
      this.miscellaneousService.updateAccountInformation(accountInformationMessage).then(() => {
        this.unchangedAccountInformation = JSON.parse(JSON.stringify(this.accountInformation))
        this.saving = false
        this.toast.success('Your account information was updated successfully.')
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to update your account information. Please try again.')
      })
    }
  }
})
</script>

<style scoped lang="scss">
.my-account-content {
  padding: 15px;
  background-color: $Color-fill-gray;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  @include desktop {
    flex-direction: row;
  }

  > .my-account-details-container {
    padding-bottom: 15px;
    position: relative;

    > .my-account-details-container-header {
      grid-area: header;
      padding-bottom: 10px;
      margin-bottom: 15px;
      border-bottom: 1px solid $Color-border-gray;
      display: flex;
      flex-direction: column;

      @include desktop {
        flex-direction: row;
      }

      > .title-text {
        line-height: 37px;
        font-size: 24px;
        font-weight: bold;
      }

      > .actions {
        position: relative;
        display: flex;
        margin-left: auto;

        > .delete-button {
          margin-left: auto;
          background-color: $Color-font-error;
          color: $Color-white;
        }

        > .save-button {
          margin-left: 15px;
        }
      }
    }

    > .loading-spinner {
      position: absolute;
      top: 50%;
      right: 50%;
      padding: 20px;
      transform: translate(50%, 50%);
    }

    > .my-account-details-container-content {
      padding-top: 15px;

      > .my-account-details-flex-container-side-by-side {
        display: flex;
        flex-direction: column;

        @include desktop {
          flex-direction: row;
        }

        > .first-item {
          margin-bottom: 15px;
          flex: 1;

          @include desktop {
            flex: 0 0 calc(50% - 10px);
            margin-right: 10px;
          }
        }

        > .second-item {
          margin-bottom: 15px;
          flex: 1;

          @include desktop {
            flex: 0 0 50%;
          }
        }
      }
    }
  }
}
</style>
